<script lang="ts" setup generic="T extends string | number | symbol">
import type { Ref } from 'vue';
import { computed, nextTick, ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';

import type {
  AppSingleSelectFontVariant,
  AppSingleSelectOption,
  AppSingleSelectSize,
  AppSingleSelectType,
} from '../models/single.model';
import SelectDropdown from '../common/SelectDropdown.vue';
import type {
  AppSvgIconSize,
  AppSvgIconType,
} from '../../appSvgIcon/AppSvgIcon.model';
import AppSvgIcon from '../../appSvgIcon/AppSvgIcon.vue';
import { AppFadeTransition } from '../../transitions';

import AppSingleSelectItem from './AppSingleSelectItem.vue';

export interface AppSingleSelectProps<T> {
  options: AppSingleSelectOption<T>[];
  selected: AppSingleSelectOption<T> | null;
  type: AppSingleSelectType;
  size?: AppSingleSelectSize;
  fontVariant?: AppSingleSelectFontVariant;
  leftSideIcon?: AppSvgIconType;
  leftSideIconSize?: AppSvgIconSize;
  placeholder?: string;
  initialLabel?: string;
  disabled?: boolean;
  wide?: boolean;
  useTeleport?: boolean;
  isWrapperScrolling?: boolean;
  extraLabel?: string;
  customWidth?: string;
}

const props = withDefaults(defineProps<AppSingleSelectProps<T>>(), {
  useTeleport: true,
});

interface AppSingleSelectEmits {
  (eventName: 'select', value: AppSingleSelectOption<T>): void;
  (eventName: 'open'): void;
  (eventName: 'left-icon-click'): void;
}

const emit = defineEmits<AppSingleSelectEmits>();

const isDropdownOpened = ref(false);
const appSingleSelectRef = ref(null);
const appSingleSelectLabelRef = ref(null);
const optionsRefs = ref();

let selected = ref(props.selected) as Ref<AppSingleSelectOption<T> | undefined>;

const selectedOptionLabel = computed(
  () => props.initialLabel || props.selected?.label || props.placeholder
);

const leftSideIconSize = computed(() => {
  if (props.leftSideIconSize) {
    return props.leftSideIconSize;
  } else {
    return props.size === 'size-s' ? '18' : '24';
  }
});

function toggleDropdown(): void {
  if (props.disabled || props.options.length === 1) {
    return;
  }

  isDropdownOpened.value = !isDropdownOpened.value;
  if (isDropdownOpened.value) {
    emit('open');
    if (props.selected) {
      scrollIntoSelected();
    }
  }
}

function onLeftIconClick(event: MouseEvent): void {
  event.stopPropagation();

  emit('left-icon-click');
}

function selectOption(option: AppSingleSelectOption<T>): void {
  isDropdownOpened.value = false;
  selected.value = option;
  emit('select', option);
}

function scrollIntoSelected(): void {
  nextTick(() => {
    const className = `option-${props.selected?.id.toString()}`;
    const element = document.getElementsByClassName(className)[0];

    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  });
}

onClickOutside(appSingleSelectRef, (event) => {
  const dropdown = document.querySelector('.app-select-dropdown');
  if (dropdown && dropdown.contains(event.target as Node)) {
    return;
  }
  if (isDropdownOpened.value) {
    isDropdownOpened.value = false;
  }
});

watch(
  () => props.isWrapperScrolling,
  () => {
    if (props.isWrapperScrolling) {
      isDropdownOpened.value = false;
    }
  }
);
</script>

<template>
  <div
    ref="appSingleSelectRef"
    class="app-single-select"
    :class="[
      props.type,
      props.size,
      props.fontVariant,
      {
        disabled: props.disabled || props.options.length < 2,
        wide: props.wide,
        opened: isDropdownOpened,
      },
    ]"
  >
    <button
      ref="appSingleSelectLabelRef"
      class="app-single-select-label"
      :style="{ width: props.wide ? '100%' : props.customWidth }"
      @click="toggleDropdown()"
    >
      <div v-if="props.leftSideIcon" class="left-side-icon">
        <AppSvgIcon
          :name="props.leftSideIcon"
          :size="leftSideIconSize"
          @click="onLeftIconClick($event)"
        />
      </div>

      <div class="app-single-select-labels">
        <span class="app-single-select-label-text">
          {{ selectedOptionLabel }}
        </span>
        <span v-if="props.extraLabel" class="app-single-select-extra-label">
          <slot name="extraLabel" />
        </span>
      </div>

      <template v-if="!props.disabled && props.options.length > 1">
        <AppSvgIcon v-if="isDropdownOpened" name="chevronUp" size="18" />
        <AppSvgIcon v-if="!isDropdownOpened" name="chevronDown" size="18" />
      </template>
    </button>

    <template v-if="props.useTeleport">
      <Teleport v-if="isDropdownOpened" to="body">
        <AppFadeTransition transition-time="very-short">
          <SelectDropdown
            v-if="isDropdownOpened"
            ref="optionsRefs"
            :parent-ref="appSingleSelectLabelRef"
            :is-using-teleport="true"
            :wide="props.wide"
          >
            <template #options>
              <AppSingleSelectItem
                v-for="item of props.options"
                :key="item.id"
                :item="item"
                :class="[`option-${item.id.toString()}`]"
                :is-selected="selected?.id === item.id"
                @click="selectOption(item)"
              />
            </template>
            <template #footer>
              <slot name="footer" />
            </template>
          </SelectDropdown>
        </AppFadeTransition>
      </Teleport>
    </template>
    <template v-else>
      <SelectDropdown
        v-if="isDropdownOpened"
        ref="optionsRefs"
        :parent-ref="appSingleSelectLabelRef"
        :is-using-teleport="false"
        :wide="props.wide"
      >
        <template #options>
          <AppSingleSelectItem
            v-for="item of props.options"
            :key="item.id"
            :item="item"
            :is-selected="selected?.id === item.id"
            :class="[`option-${item.id.toString()}`]"
            @click="selectOption(item)"
          />
        </template>
        <template #footer>
          <slot name="footer" />
        </template>
      </SelectDropdown>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import '../../../styles/_media';
@import '../../../styles/_fonts.scss';

.app-single-select {
  position: relative;
  display: flex;
  height: fit-content;
  white-space: nowrap;

  align-items: center;

  * {
    box-sizing: border-box;
  }

  &.default {
    .app-single-select-label {
      background-color: var(--color-white);
      color: var(--color-neutral-70);

      border-radius: var(--border-radius-8);
      border: 1px solid var(--color-neutral-70);
    }
  }

  &.modern {
    .app-single-select-label {
      background-color: var(--color-white);
      color: var(--color-neutral-70);

      border-radius: var(--border-radius-8);
      border: 1px solid var(--color-scrollbar);
      gap: var(--spacing-4);
      @include on-hover {
        background-color: var(--color-neutral-10);
      }
    }
    &.opened {
      .app-single-select-label {
        background-color: var(--color-white);
        border: 1px solid var(--color-soft-black);
      }
    }
  }

  &.primary {
    .app-single-select-label {
      border-radius: var(--border-radius-8);
      background-color: var(--color-neutral-70);
      color: var(--color-white);
      border: 1px solid var(--color-neutral-70);
    }
  }

  &.transparent {
    .app-single-select-label {
      background-color: transparent;
      color: var(--color-neutral-70);
    }
  }

  &.light {
    .app-single-select-label {
      background-color: var(--color-white);
      border-radius: var(--border-radius-8);
      color: var(--color-neutral-70);
      &:not(.disabled) {
        @include on-hover {
          background-color: var(--color-neutral-30);
        }
      }
    }
  }

  &.grey {
    .app-single-select-label {
      background-color: var(--color-neutral-30);
      border-radius: var(--border-radius-8);
      color: var(--color-neutral-70);
      &:not(.disabled) {
        @include on-hover {
          background-color: var(--color-neutral-30);
        }
      }
    }
  }

  &.summer {
    .app-single-select-label {
      background-color: var(--color-brand-summer);
      border-radius: var(--border-radius-8);

      color: var(--color-neutral-70);
      &:not(.disabled) {
        @include on-hover {
          background-color: var(--color-brand-summer-hover);
        }
      }
    }
  }

  &.font-light {
    .app-single-select-label {
      @include font-label-s-light;
    }
  }

  &.font-bold {
    .app-single-select-label {
      @include font-label-m-bold;
    }
  }

  &.font-small {
    .app-single-select-label {
      @include font-label-s-bold;
    }
  }

  &.font-button-small {
    .app-single-select-label {
      @include font-button-small;
    }
  }

  &.size-s {
    .app-single-select-label {
      padding: var(--spacing-4);
    }
  }
  &.size-m {
    .app-single-select-label {
      padding: var(--spacing-8) var(--spacing-16);
    }
  }
  &.size-l {
    .app-single-select-label {
      padding: var(--spacing-16);
    }
  }

  &.disabled {
    .app-single-select-label {
      cursor: default;
      transition: none;
    }
  }

  &.wide {
    width: 100%;
    .app-single-select-label {
      width: 100%;
    }
  }

  .app-single-select-label {
    all: unset;
    box-sizing: border-box;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--spacing-8);
    cursor: pointer;

    padding: var(--spacing-8) var(--spacing-12);
    @include font-label-s-bold;

    .app-single-select-labels {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      .app-single-select-extra-label {
        @include font-label-s-light;
      }
    }
  }
}
</style>
